import { gql, useMutation, useQuery } from '@apollo/client'
import PageComponent from 'components/Dashboard/PageComponent'
import { format, parseISO } from 'date-fns'
import { Field, Form, Formik } from 'formik'
import { isEmpty } from 'lodash'
import * as React from 'react'
import { StringParam, useQueryParam } from 'use-query-params'
import SubmitButton from '../../SubmitButton'
import TextInput from '../../TextInput'
import { ActiveUserSessions } from './__generated__/ActiveUserSessions'
import { RapidSessionDetailsForEntryFragment } from './__generated__/RapidSessionDetailsForEntryFragment'
import { SessionDetails, SessionDetailsVariables } from './__generated__/SessionDetails'

const rapidSessionFragment = gql`
  fragment RapidSessionDetailsForEntryFragment on RapidSession {
    id
    startDate
    endDate
    reference
    thirdPartyId
    kitCount {
      positive
      negative
    }
  }
`

const sessionDetailsQuery = gql`
  query SessionDetails($sessionId: ID!) {
    rapidSession(id: $sessionId) {
      id
      ...RapidSessionDetailsForEntryFragment
    }
  }

  ${rapidSessionFragment}
`

const userSessionsQuery = gql`
  query ActiveUserSessions {
    rapidSessions {
      id
      ...RapidSessionDetailsForEntryFragment
    }
  }

  ${rapidSessionFragment}
`

const kitRegistrationInfoQuery = gql`
  query KitRegistrationInfoForRapidResult($kitId: ID!) {
    registrationInfo(kitId: $kitId) {
      kitId
      sku
      status
      testPartner {
        id
        consentText
      }
    }
  }
`

const addResultMutation = gql`
  mutation AddResult($input: AddResultInput!) {
    addResult(input: $input) {
      success
      rapidSession {
        id
        kitCount {
          positive
          negative
        }
      }
    }
  }
`

const KitLookup = ({ detailsRetrieved }: { detailsRetrieved: (registrationInfo: Record<string, unknown>) => void }) => {
  // TODO: Loading state
  const { refetch } = useQuery(kitRegistrationInfoQuery, {
    skip: true,
  })

  return (
    <div>
      <Formik
        initialValues={{ kitId: '' }}
        onSubmit={async (values) => {
          const { data } = await refetch({ kitId: values.kitId.trim() })

          detailsRetrieved(data.registrationInfo)
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <TextInput
              name="kitId"
              placeholder="Kit ID to find"
              type="text"
              addons={
                <SubmitButton className="is-primary" submitting={isSubmitting}>
                  Search
                </SubmitButton>
              }
            />
          </Form>
        )}
      </Formik>
    </div>
  )
}

const ResultCapture = ({
  sessionId,
  kitId,
  onComplete,
}: {
  sessionId: string
  kitId: string
  onComplete: () => void
}) => {
  const [addResult] = useMutation(addResultMutation)

  return (
    <div>
      <Formik
        initialValues={{ result: '', notes: '' }}
        onSubmit={async (values) => {
          const result = await addResult({
            variables: {
              input: {
                kitId,
                result: values.result,
                notes: values.notes,
                sessionId,
              },
            },
          })

          console.log('RESULT', result)
          onComplete()
        }}
      >
        {({ isSubmitting }) => (
          <Form>
            <div className="field">
              <label className="label">Result</label>

              <label className="radio">
                <Field type="radio" name="result" value="POSITIVE" /> POSITIVE
              </label>
              <br />
              <label className="radio">
                <Field type="radio" name="result" value="NEGATIVE" /> NEGATIVE
              </label>
              <br />
              <label className="radio">
                <Field type="radio" name="result" value="OTHER" /> OTHER (explain in notes)
              </label>

              <Field name="result">
                {({ meta: { touched, error } }) =>
                  touched && error ? <p className="help is-danger">{error}</p> : null
                }
              </Field>
            </div>

            <TextInput name="notes" label="Notes" />

            <SubmitButton className="is-primary" submitting={isSubmitting}>
              Submit Result
            </SubmitButton>
          </Form>
        )}
      </Formik>
    </div>
  )
}

const allowReg = (kitDetails: { sku: string; status: string }): [boolean, string?] => {
  if (!['rapid_ag', 'rapid_travel'].includes(kitDetails.sku)) {
    return [false, `This is a ${kitDetails.sku} kit, not a rapid testing kit`]
  }

  if (!['CREATED', 'UNREGISTERED', 'REGISTERED'].includes(kitDetails.status)) {
    return [false, `Wrong kit status: ${kitDetails.status}`]
  }

  return [true]
}

const CaptureResults: PageComponent = () => {
  const [kitDetails, setKitDetails] = React.useState()

  const [sessionId] = useQueryParam('sessionId', StringParam)

  const { data: activeSessionsData, loading: activeSessionsLoading } = useQuery<ActiveUserSessions>(userSessionsQuery, {
    skip: !isEmpty(sessionId),
  })

  const { data: sessionData, loading: sessionLoading } = useQuery<SessionDetails, SessionDetailsVariables>(
    sessionDetailsQuery,
    {
      variables: { sessionId },
      skip: isEmpty(sessionId),
    }
  )

  if (activeSessionsLoading || sessionLoading) {
    return <p>Loading session...</p>
  }

  const activeSession = activeSessionsData?.rapidSessions.length === 1 ? activeSessionsData?.rapidSessions[0] : null

  const s: RapidSessionDetailsForEntryFragment | null = activeSession || sessionData?.rapidSession

  const thirdParty = s?.thirdPartyId

  const [isRegAllowed, regNotAllowedMessage] = kitDetails ? allowReg(kitDetails) : []

  return (
    <section className="section">
      <div className="container">
        {activeSessionsData?.rapidSessions.length > 1 && (
          <div className="card mb-5">
            <div className="card-content">
              <div className="media">
                <div className="media-content">
                  <p>Multiple active sessions, assuming no session</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {thirdParty && (
          <div className="card mb-5">
            <div className="card-content">
              <div className="media">
                <div className="media-content">
                  <p>
                    Collecting results for: <strong>{thirdParty}</strong>
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {s ? (
          <div className="card mb-5">
            <div className="card-content">
              <div className="media">
                <div className="media-content">
                  <p className="title is-4">
                    {format(parseISO(s.startDate), 'dd/MM/yyyy')} &mdash; {s.reference}
                  </p>
                </div>
              </div>

              <div className="content">
                <p>
                  <strong>{s.kitCount.negative + s.kitCount.positive} Results </strong>({s.kitCount.negative} neg,{' '}
                  {s.kitCount.positive} pos)
                </p>
              </div>
            </div>
          </div>
        ) : null}

        <KitLookup detailsRetrieved={(details) => setKitDetails(details)} />

        {kitDetails && (
          <div>
            <p>
              <strong>Kit ID: </strong>
              {kitDetails.kitId}
            </p>
            <p>
              <strong>Kit status: </strong>
              {kitDetails.status}
            </p>
          </div>
        )}

        {isRegAllowed === false && (
          <article className="message is-danger">
            <div className="message-body">{regNotAllowedMessage}</div>
          </article>
        )}

        {isRegAllowed && (
          <ResultCapture sessionId={s?.id} kitId={kitDetails.kitId} onComplete={() => setKitDetails(undefined)} />
        )}
      </div>
    </section>
  )
}

CaptureResults.displayName = 'Capture Rapid Testing Results'
CaptureResults.path = '/rapid-testing/capture-results'
CaptureResults.allowedGroups = ['dashboard', 'addresults']

export default CaptureResults
